/**
 * Canonical indexer image modes — single source of truth for every admin UI
 * surface that lets an admin pick an image strategy (Knowledge Base sync/
 * rebuild controls, Knowledge Source creation/re-index controls).
 *
 * Must stay in sync with agent_configs.py's `ImageMode` type and
 * indexingAdmin.js's `VALID_IMAGE_MODES`. See AA-218/219/220,
 * docs/standards/agent-image-citation-contract.md §5-6, and
 * docs/architecture/IMAGE_MODE_REFERENCE.md for the full cost model behind
 * each strategy.
 */

export type ImageModeChoice =
  | 'default' | 'skip' | 'caption' | 'marker'
  | 'bbox_horizontal' | 'bbox_vertical' | 'page_image' | 'auto';

export interface ImageModeOption {
  value: ImageModeChoice;
  label: string;
  visionHeavy: boolean;
  /** One-line explanation of what the strategy does. */
  description: string;
  /** Concrete example of when to pick it. */
  example: string;
  /** Rough per-run cost hint shown next to the description. */
  costHint: string;
  /** Document types/situations this strategy handles well. */
  goodFor: string[];
  /** Document types/situations where this strategy is a poor fit. */
  notGoodFor: string[];
}

export const IMAGE_MODE_OPTIONS: ImageModeOption[] = [
  {
    value: 'default',
    label: 'Default (agent config)',
    visionHeavy: false,
    description: "Inherit whatever image_mode is already configured for this agent in agent_configs.py — sends no override.",
    example: 'Use this unless you have a specific reason to try something else. Safest choice for a first index.',
    costHint: '$0 extra (uses existing config)',
    goodFor: ['Routine re-indexes of an agent that already has a working, tuned image strategy'],
    notGoodFor: ["Brand-new agents with no agent_configs.py entry yet — there's nothing to inherit"],
  },
  {
    value: 'skip',
    label: 'Skip (no images)',
    visionHeavy: false,
    description: 'No image extraction at all — images are ignored entirely, no vision calls.',
    example: "Agents whose answers never need images, e.g. HR Policy, PWMS Support, Product, Voucher, ERP FAQ.",
    costHint: '$0',
    goodFor: ['Pure-text policy/reference documents', 'Keeping indexing time and cost minimal when images add no value'],
    notGoodFor: ['Work instructions, SOPs, or manuals where a screenshot/diagram IS the answer — those users lose all figures'],
  },
  {
    value: 'marker',
    label: 'Marker (position only, no vision)',
    visionHeavy: false,
    description: "Extracts each image's position but does not caption it — just leaves a [pic] marker in the text where it appeared.",
    example: 'Text-native PDFs where the images are decorative and add no real information (letterheads, dividers).',
    costHint: '$0',
    goodFor: ['Documents dominated by letterheads/dividers/decorative art', 'A cheap first pass before deciding a vision-heavy mode is worth the cost'],
    notGoodFor: ['Any document where the image content itself matters — a [pic] marker with no caption is invisible to search and to the model'],
  },
  {
    value: 'caption',
    label: 'Caption (per-image, {visionModel})',
    visionHeavy: true,
    description: 'Captions each embedded image individually with {visionModel} Vision (low detail) and embeds the caption at that spot in the page text.',
    example: '"Normal" PDFs/DOCX where embedded images are meaningful units — photos, diagrams, screenshots. This is the historical default (UniFirst Docs, Service Desk).',
    costHint: '~$0.0015 per image',
    goodFor: ['Standard PDFs/DOCX with discrete embedded images (screenshots, product photos, diagrams)', 'Documents where each image is a self-contained unit of meaning'],
    notGoodFor: ['Image-only/scanned/slide-export PDFs with no extractable embedded images — use Full-page rasterize + OCR instead', "Documents where layout/position relative to text matters — captions here don't preserve spatial placement (use a bbox_* mode)"],
  },
  {
    value: 'bbox_horizontal',
    label: 'Side-by-side crop (horizontal)',
    visionHeavy: true,
    description: 'Same per-image captioning as Caption, but also emits bounding-box data so the UI can render the image next to its associated text, side by side.',
    example: 'Manuals with a real spatial layout worth preserving, e.g. Direct Embroidery production work instructions.',
    costHint: '~$0.0015 per image',
    goodFor: ['Wide layouts where the image naturally sits beside its instructions (e.g. landscape manuals, two-column work instructions)'],
    notGoodFor: ['Narrow/portrait content where a side-by-side crop would be cramped — try the vertical variant instead', 'Documents where images and text are only loosely related (spatial association will be noisy)'],
  },
  {
    value: 'bbox_vertical',
    label: 'Side-by-side crop (vertical)',
    visionHeavy: true,
    description: 'Same as the horizontal variant, but stacks the image above/below its associated text instead of side by side.',
    example: 'Same use case as the horizontal variant — pick whichever orientation matches the source documents better.',
    costHint: '~$0.0015 per image',
    goodFor: ['Portrait/narrow layouts (typical single-column PDFs) where stacking reads more naturally than side-by-side'],
    notGoodFor: ['Wide landscape layouts — the horizontal variant usually fits those better', 'Documents where images and text are only loosely related (spatial association will be noisy)'],
  },
  {
    value: 'page_image',
    label: 'Full-page rasterize + OCR',
    visionHeavy: true,
    description: 'Rasterizes the entire page to a PNG and runs one combined caption+OCR Vision call (high detail) on the page image, instead of extracting individual embedded images.',
    example: 'Image-only / fragmented-slide PDFs where individual "embedded images" are not meaningful units — e.g. Articulate/slide exports, scanned manuals.',
    costHint: '~$0.006 per page',
    goodFor: ['Scanned documents and image-only PDFs with no extractable text layer', 'Slide/Articulate exports where each "page" is really one big flattened graphic'],
    notGoodFor: ['Long text-native documents — this is the most expensive mode per page and text extraction already works fine there', 'Documents with many small discrete images per page — those get flattened into one page caption instead of individually described'],
  },
  {
    value: 'auto',
    label: 'Auto-detect per page',
    visionHeavy: true,
    description: 'Per-document heuristic: text-native pages get Marker (free), image-only/fragmented pages get Full-page rasterize + OCR.',
    example: "Mixed folders where you don't want to hand-classify every document — currently used by First Aid & Safety (Articulate-exported SOPs mixed with normal PDFs).",
    costHint: '~$0.006 per capped page',
    goodFor: ['Mixed folders combining normal PDFs and slide/scan exports, where hand-picking a mode per document is impractical'],
    notGoodFor: ['A folder that is uniformly one document type — pick Caption or Full-page rasterize + OCR directly instead for more predictable cost/behavior'],
  },
];

export const isVisionHeavy = (choice: ImageModeChoice): boolean =>
  IMAGE_MODE_OPTIONS.find(o => o.value === choice)?.visionHeavy ?? false;

/** Default shown before the live `visionModel` (from the Function App's
 * /api/agent-config, proxied through /agent-image-configs) has loaded — this
 * is also literally the code default (AZURE_OPENAI_VISION_DEPLOYMENT unset),
 * so it's never a "wrong" placeholder, just possibly stale for one paint. */
export const DEFAULT_VISION_MODEL_LABEL = 'gpt-4o';

const withVisionModel = (text: string, visionModel: string): string =>
  text.replace(/\{visionModel\}/g, visionModel);

/**
 * Resolve `{visionModel}` placeholders in label/description copy against the
 * actual configured deployment name (AA-220 follow-up — "is 4o hardcoded in
 * the UI or via the variable?"). Previously "GPT-4o" was static copy with no
 * connection to the backend's AZURE_OPENAI_VISION_DEPLOYMENT app setting;
 * this makes the UI text track it instead of silently going stale.
 */
export const getResolvedImageModeOptions = (
  visionModel: string = DEFAULT_VISION_MODEL_LABEL
): ImageModeOption[] =>
  IMAGE_MODE_OPTIONS.map(o => ({
    ...o,
    label: withVisionModel(o.label, visionModel),
    description: withVisionModel(o.description, visionModel),
  }));

export const getImageModeOption = (
  choice: ImageModeChoice,
  visionModel: string = DEFAULT_VISION_MODEL_LABEL
): ImageModeOption | undefined =>
  getResolvedImageModeOptions(visionModel).find(o => o.value === choice);

/** Map the UI choice to the value Express expects ('default' → omit override). */
export const captionToImageMode = (choice: ImageModeChoice): string | undefined =>
  choice === 'default' ? undefined : choice;
